'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type KeyboardEvent, type MouseEvent, useEffect, useState } from 'react';
import { usePreviewSimulation } from '@/components/launchpad/use-preview-simulation';
import { MarketRowCardView, type OutcomeRowSummary } from '@/components/markets/market-row-card';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { useProposalFormStore } from '@/stores/proposal-form';

/**
 * Stable placeholder pubkey for the pre-mint preview. The proposal hasn't
 * been deployed yet so there's no on-chain account; the wrapper below uses
 * `pointer-events-none` to prevent the card's click handler from trying to
 * router-push to a non-existent market route.
 */
const PREVIEW_PUBKEY = 'preview';

/**
 * Canonical Morfi consensus threshold (66%, D9-encoded). Keeps the preview's
 * bar scaling identical to the live markets feed.
 */
const PREVIEW_CONSENSUS_THRESHOLD_D9 = '660000000';

// Suppress card-level navigation but keep hover/pointer events live so the
// mcap reveal still fires on each bar. `pointer-events-none` on the
// wrapper would block both — we only want to block the click.
function suppressNavigation(e: MouseEvent | KeyboardEvent): void {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * Hidden hook adapter — resolves NSFW status for a single URL via
 * `useNsfwStatusByUrl` and reports the result up. Rendered with `key={url}`
 * so a URL change remounts (and hooks restart) cleanly. Renders nothing.
 *
 * This pattern lets the parent collect per-URL NSFW flags without violating
 * Rules of Hooks (which forbid calling hooks inside `.map()` over a dynamic
 * array).
 */
function NsfwUrlResolver(props) {
  const $ = _c(5);
  const {
    url,
    onResolve
  } = props;
  const {
    isNsfw
  } = useNsfwStatusByUrl(url);
  let t0;
  let t1;
  if ($[0] !== isNsfw || $[1] !== onResolve || $[2] !== url) {
    t0 = () => {
      onResolve(url, isNsfw);
    };
    t1 = [url, isNsfw, onResolve];
    $[0] = isNsfw;
    $[1] = onResolve;
    $[2] = url;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * Pre-mint preview surface — renders the user's proposal exactly the way it
 * will appear in the markets feed, with a small simulation engine driving
 * volume / prize-pool / share / live-trade animation so the card reads as a
 * live market rather than a static placeholder.
 *
 * The card itself is interactive on the live feed (clicks navigate, bookmark
 * toggles persist). Here it's wrapped in `pointer-events-none` so the
 * preview is purely visual — there's no on-chain market to route to yet.
 *
 * NSFW gating: the cover image and each outcome thumbnail are checked
 * against the moderation pipeline via `useNsfwStatusByUrl`. The cover keeps
 * the standard tap-to-reveal behaviour. The outcome thumbnails get the blur
 * but no reveal button — the creator already knows what they uploaded; the
 * blur is a flag, not a gate.
 */
export function CreateProposalStepPreview(t0) {
  const $ = _c(31);
  const {
    categoryOverride
  } = t0;
  const t = useTranslations("launchpad.form");
  const stackedMarketCards = useFeatureGate("stacked_market_cards") === true;
  const store = useProposalFormStore();
  const filledOutcomes = store.outcomes.filter(_temp);
  const sim = usePreviewSimulation(filledOutcomes.length);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [nsfwByUrl, setNsfwByUrl] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleNsfwResolve(url, isNsfw) {
      setNsfwByUrl(prev => prev[url] === isNsfw ? prev : {
        ...prev,
        [url]: isNsfw
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNsfwResolve = t2;
  const coverDurableUrl = store.coverImageUrl;
  const coverDisplayUrl = store.coverImagePreview !== undefined && store.coverImagePreview.length > 0 ? store.coverImagePreview : coverDurableUrl;
  const outcomeUrls = filledOutcomes.map(_temp2).filter(_temp3);
  const allUrls = Array.from(new Set([...(coverDurableUrl !== "" ? [coverDurableUrl] : []), ...outcomeUrls]));
  const coverIsNsfw = coverDurableUrl !== "" && store.coverImageDraft === null ? nsfwByUrl[coverDurableUrl] ?? true : false;
  let t3;
  if ($[2] !== nsfwByUrl || $[3] !== sim.outcomes || $[4] !== t) {
    t3 = (o_1, i) => {
      const simOutcome = sim.outcomes[i];
      const durableUrl = o_1.imageUrl !== "" ? o_1.imageUrl : null;
      const displayUrl = o_1.imagePreview !== undefined && o_1.imagePreview.length > 0 ? o_1.imagePreview : durableUrl;
      return {
        label: o_1.label.length > 0 ? o_1.label : t("outcomeLabel", {
          number: i + 1
        }),
        share: simOutcome?.share ?? 0,
        originalIndex: i,
        effectiveMarketCapD6: simOutcome?.reservesD6 ?? "0",
        imageUrl: displayUrl,
        imageNsfw: durableUrl !== null ? nsfwByUrl[durableUrl] ?? true : false
      };
    };
    $[2] = nsfwByUrl;
    $[3] = sim.outcomes;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const outcomes = filledOutcomes.map(t3);
  let t4;
  if ($[6] !== allUrls || $[7] !== handleNsfwResolve) {
    t4 = allUrls.map(url_0 => <NsfwUrlResolver key={url_0} url={url_0} onResolve={handleNsfwResolve} />);
    $[6] = allUrls;
    $[7] = handleNsfwResolve;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = store.title.length > 0 ? store.title : "\u2014";
  const t6 = store.location.length > 0 ? store.location : null;
  let t7;
  if ($[9] !== categoryOverride || $[10] !== store.category) {
    t7 = (categoryOverride ?? store.category).length > 0 ? (categoryOverride ?? store.category).toLowerCase() : null;
    $[9] = categoryOverride;
    $[10] = store.category;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = sim.volumeTotalD6;
  const t9 = sim.prizePoolD6;
  const t10 = sim.uniqueTradersTotal;
  let t11;
  let t12;
  if ($[12] !== sim.liveTrades) {
    t11 = [...sim.liveTrades];
    t12 = sim.liveTrades.map(_temp4);
    $[12] = sim.liveTrades;
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  const t13 = stackedMarketCards ? "stacked" : "bars";
  let t14;
  if ($[15] !== coverDisplayUrl || $[16] !== coverIsNsfw || $[17] !== outcomes || $[18] !== sim.prizePoolD6 || $[19] !== sim.uniqueTradersTotal || $[20] !== sim.volumeTotalD6 || $[21] !== t11 || $[22] !== t12 || $[23] !== t13 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t14 = <MarketRowCardView linkDisabled={true} elevatedCardChrome={true} pubkey={PREVIEW_PUBKEY} title={t5} coverImageUrl={coverDisplayUrl} coverImageNsfw={coverIsNsfw} location={t6} category={t7} volumeTotal={t8} prizePool={t9} uniqueTradersTotal={t10} outcomes={outcomes} position={0} activeSort="volume24h" consensusThreshold={PREVIEW_CONSENSUS_THRESHOLD_D9} liveTrades={t11} liveSinceMountIds={t12} outcomeLayout={t13} surface="card" suppressOutcomeReveal={true} />;
    $[15] = coverDisplayUrl;
    $[16] = coverIsNsfw;
    $[17] = outcomes;
    $[18] = sim.prizePoolD6;
    $[19] = sim.uniqueTradersTotal;
    $[20] = sim.volumeTotalD6;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t4) {
    t15 = <div className="mx-auto w-full max-w-md" onClickCapture={suppressNavigation} onKeyDownCapture={suppressNavigation}>{t4}{t14}</div>;
    $[28] = t14;
    $[29] = t4;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp4(t_0) {
  return t_0.id;
}
function _temp3(u) {
  return u !== "" && u !== undefined && u !== null;
}
function _temp2(o_0) {
  return o_0.imageUrl;
}
function _temp(o) {
  return o.label.trim().length > 0;
}